import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import orderService from '../../../services/order.service';
import reviewService from '../../../services/review.service';
import uploadService from '../../../services/upload.service';
import {
  Package,
  ChevronLeft,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  MapPin,
  User,
  CreditCard,
  Receipt,
  Download,
  AlertCircle,
  Loader,
  ChevronRight,
  Calendar,
  ShoppingBag,
  Phone,
  Mail,
  Smartphone,
  Wallet,
  DollarSign,
  MessageCircle,
  ThumbsUp,
  ThumbsDown,
  Star,
  Check,
  X,
  Upload,
  Camera,
  QrCode
} from 'lucide-react';
import { showSuccess, showError, showLoading, dismissToast } from '../../../utils/toast';
import paymaya_logo from '../../../assets/paymaya_logo.png';
import gcash_logo from '../../../assets/gcash_logo.png';
import qrCode_gcash from '../../../qr/qrCode_gcash.jpg';
import qrCode_paymaya from '../../../qr/qrCode_Paymaya.jpg';

interface OrderItemDisplay {
  id: number;
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  price: number;
  imageUrl: string;
}

interface OrderDisplay {
  id: number;
  orderNumber: string;
  orderDate: string;
  totalAmount: number;
  status: string;
  paymentMethod: string;
  paymentStatus: string;
  shippingAddress: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  items: OrderItemDisplay[];
  trackingNumber?: string;
  paymentProofImage?: string;
  paymentProofReference?: string;
  paymentProofSender?: string;
  paymentProofDate?: string;
  paymentProofNotes?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  deliveryProofImageUrl?: string;
}

interface RatedProduct {
  productId: number;
  rating: number;
  reviewId?: number;
}

// Shop payment details for display
const SHOP_PAYMENT_DETAILS = {
  gcash: {
    name: 'GCash',
    accountName: 'Doctama Marketing',
    accountNumber: '09123456789',
    qrCode: qrCode_gcash,
  },
  paymaya: {
    name: 'PayMaya',
    accountName: 'Doctama Marketing',
    accountNumber: '09123456789',
    qrCode: qrCode_paymaya,
  }
};

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [order, setOrder] = useState<OrderDisplay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [ratedProducts, setRatedProducts] = useState<Map<number, RatedProduct>>(new Map());
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<OrderItemDisplay | null>(null);
  const [selectedRating, setSelectedRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingTitle, setRatingTitle] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [existingReviews, setExistingReviews] = useState<Map<number, any>>(new Map());
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [canReview, setCanReview] = useState<{ canReview: boolean; reason?: string } | null>(null);

  // Complete Payment states
  const [showCompletePaymentModal, setShowCompletePaymentModal] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string>('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [senderName, setSenderName] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);

  useEffect(() => {
    if (id) {
      loadOrder();
    } else {
      setError('Invalid order ID');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (order?.status?.toLowerCase() === 'delivered' && order.items?.length > 0) {
      checkExistingReviews();
    }
  }, [order]);

  // Auto-refresh every 30 seconds to get updated status (especially important for GCash payments)
  useEffect(() => {
    const interval = setInterval(() => {
      if (order && !loading) {
        loadOrder(true); // Silent refresh
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [order, loading]);

  const loadOrder = async (silent: boolean = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError('');
    try {
      const orderId = parseInt(id as string);
      
      if (isNaN(orderId)) {
        throw new Error('Invalid order ID format');
      }
      
      const data = await orderService.getOrderById(orderId);
      
      if (!data) {
        if (!silent) {
          setError('Order not found');
        }
      } else {
        const displayOrder: OrderDisplay = {
          id: typeof data.id === 'string' ? parseInt(data.id) : data.id,
          orderNumber: data.orderNumber,
          orderDate: data.orderDate,
          totalAmount: data.totalAmount,
          status: data.status,
          paymentMethod: data.paymentMethod,
          paymentStatus: data.paymentStatus,
          shippingAddress: data.shippingAddress || '',
          customerName: data.customerName,
          customerEmail: data.customerEmail,
          customerPhone: data.customerPhone,
          items: data.items?.map((item: any) => ({
            id: typeof item.id === 'string' ? parseInt(item.id) : (item.id || 0),
            productId: typeof item.productId === 'string' ? parseInt(item.productId) : (item.productId || 0),
            productName: item.productName || '',
            quantity: item.quantity || 0,
            unitPrice: item.unitPrice || item.price || 0,
            price: item.price || item.unitPrice || 0,
            imageUrl: item.imageUrl || ''
          })) || [],
          trackingNumber: (data as any).trackingNumber,
          paymentProofImage: (data as any).paymentProofImage,
          deliveryProofImageUrl: (data as any).deliveryProofImageUrl,
          paymentProofReference: (data as any).paymentProofReference,
          paymentProofSender: (data as any).paymentProofSender,
          paymentProofDate: (data as any).paymentProofDate,
          paymentProofNotes: (data as any).paymentProofNotes,
          approvedBy: (data as any).approvedBy,
          approvedAt: (data as any).approvedAt,
          rejectedBy: (data as any).rejectedBy,
          rejectionReason: (data as any).rejectionReason
        };
        
        // Check if status changed for silent refresh
        if (silent && order) {
          const statusChanged = 
            order.status !== displayOrder.status || 
            order.paymentStatus !== displayOrder.paymentStatus ||
            order.approvedBy !== displayOrder.approvedBy ||
            order.rejectedBy !== displayOrder.rejectedBy;
          
          if (statusChanged) {
            showSuccess('Order status updated!');
          }
        }
        
        setOrder(displayOrder);
      }
    } catch (error: any) {
      console.error('❌ Failed to load order:', error);
      if (!silent) {
        setError(error.message || 'Failed to load order');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const checkExistingReviews = async () => {
    if (!order?.items) return;
    
    setLoadingReviews(true);
    const reviewsMap = new Map();
    
    try {
      for (const item of order.items) {
        const reviews = await reviewService.getProductReviews(item.productId);
        const userReview = reviews.find((r: any) => r.userName === order.customerName);
        
        if (userReview) {
          reviewsMap.set(item.productId, userReview);
          ratedProducts.set(item.productId, {
            productId: item.productId,
            rating: userReview.rating,
            reviewId: userReview.id
          });
        }
      }
      setExistingReviews(reviewsMap);
      setRatedProducts(new Map(ratedProducts));
    } catch (error) {
      console.error('Failed to check existing reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showError('File too large. Maximum size is 5MB.');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showError('Please upload an image file.');
      return;
    }

    setReceiptFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setReceiptPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCompletePayment = async () => {
  if (!order) return;
  
  if (!receiptFile) {
    showError('Please upload your payment receipt');
    return;
  }
  
  if (!referenceNumber.trim()) {
    showError('Please enter the reference number');
    return;
  }
  
  if (!senderName.trim()) {
    showError('Please enter the sender name');
    return;
  }
  
  setSubmittingPayment(true);
  const loadingToast = showLoading('Submitting payment proof...');
  
  try {
    const uploadedUrls = await uploadService.uploadImages([receiptFile]);
    const receiptImageUrl = uploadedUrls[0];
    
    // ✅ Use the dedicated uploadPaymentProof method
    await orderService.uploadPaymentProof(order.id, {
      paymentProofImage: receiptImageUrl,
      paymentProofReference: referenceNumber,
      paymentProofSender: senderName,
      paymentProofDate: paymentDate,
      paymentProofNotes: ''
    });
    
    dismissToast(loadingToast);
    showSuccess('Payment proof submitted! Your payment is now under review.');
    
    setShowCompletePaymentModal(false);
    setReceiptFile(null);
    setReceiptPreview('');
    setReferenceNumber('');
    setSenderName('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    
    await loadOrder();
  } catch (error: any) {
    dismissToast(loadingToast);
    console.error('Payment proof upload error:', error);
    showError(error.response?.data?.message || error.message || 'Failed to submit payment proof');
  } finally {
    setSubmittingPayment(false);
  }
};
  const handleRateProduct = (product: OrderItemDisplay) => {
    const existing = existingReviews.get(product.productId);
    if (existing) {
      showError(`You already reviewed ${product.productName}`);
      return;
    }

    // For delivered and paid orders, allow review without API check
    const isDeliveredOrder = order?.status?.toLowerCase() === 'delivered';
    const isPaidOrder = order?.paymentStatus?.toLowerCase() === 'paid' || order?.paymentMethod === 'cod';

    if (!isDeliveredOrder || !isPaidOrder) {
      showError('You can only review products from delivered and paid orders');
      return;
    }

    console.log('✅ Allowing review for delivered and paid order');
    setSelectedProduct(product);
    setSelectedRating(ratedProducts.get(product.productId)?.rating || 0);
    setRatingComment('');
    setRatingTitle('');
    setShowRatingModal(true);
  };

  const handleSubmitRating = async () => {
    if (!selectedProduct) return;
    
    if (selectedRating === 0) {
      showError('Please select a rating');
      return;
    }
    
    if (!ratingComment.trim()) {
      showError('Please write a review comment');
      return;
    }
    
    setSubmittingRating(true);
    const loadingToast = showLoading('Submitting review...');
    
    try {
      await reviewService.createReview({
        productId: selectedProduct.productId,
        rating: selectedRating,
        title: ratingTitle,
        comment: ratingComment,
        images: []
      });
      
      dismissToast(loadingToast);
      showSuccess(`Thank you for reviewing ${selectedProduct.productName}!`);
      
      ratedProducts.set(selectedProduct.productId, {
        productId: selectedProduct.productId,
        rating: selectedRating
      });
      setRatedProducts(new Map(ratedProducts));
      
      setShowRatingModal(false);
      setSelectedProduct(null);
      setSelectedRating(0);
      setRatingComment('');
      setRatingTitle('');
      
      checkExistingReviews();
    } catch (error: any) {
      dismissToast(loadingToast);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to submit review';
      console.error('❌ Review submission error:', error.response?.data);
      showError(errorMessage);
    } finally {
      setSubmittingRating(false);
    }
  };

  const renderStars = (rating: number, size: number = 5, interactive: boolean = false, onStarClick?: (rating: number) => void) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onStarClick?.(star)}
            disabled={!interactive}
            className={`${interactive ? 'cursor-pointer hover:scale-110 transition' : 'cursor-default'}`}
          >
            <Star
              className={`w-${size} h-${size} ${
                star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const getDisplayStatus = () => {
  if (!order) return '';

  if (order.rejectedBy && order.paymentStatus === 'failed') {
    return 'PAYMENT FAILED';
  }

  if (order.approvedBy && order.paymentStatus === 'paid') {
    if (order.status === 'delivered') return 'DELIVERED';
    if (order.status === 'shipped' || order.status === 'outfordelivery') return 'SHIPPED';
    return 'PROCESSING';
  }

  return (order.status || '').replace(/_/g, ' ').toUpperCase();
};

  const getDisplayStatusColor = () => {
  if (!order) return '';
  
  const status = order.status?.toLowerCase() || '';
  
  if (order.rejectedBy && order.paymentStatus === 'failed') {
    return 'bg-red-100 text-red-800 border-red-200';
  }
  
  if (order.approvedBy && order.paymentStatus === 'paid') {
    return 'bg-green-100 text-green-800 border-green-200';
  }
  
  switch(status) {
    case 'delivered': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'shipped':
    case 'outfordelivery': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'processing': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'awaiting_payment': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'cancelled': return 'bg-rose-100 text-rose-800 border-rose-200';
    default: return 'bg-gray-100 text-gray-800';
  }
};

  const getPaymentStatusDisplay = (paymentStatus: string, rejectedBy?: string) => {
    if (rejectedBy && paymentStatus === 'failed') {
      return { text: 'FAILED', color: 'bg-red-100 text-red-800' };
    }
    switch(paymentStatus) {
      case 'paid': return { text: 'PAID', color: 'bg-green-100 text-green-800' };
      case 'pending': return { text: 'PENDING', color: 'bg-yellow-100 text-yellow-800' };
      case 'failed': return { text: 'FAILED', color: 'bg-red-100 text-red-800' };
      case 'refunded': return { text: 'REFUNDED', color: 'bg-purple-100 text-purple-800' };
      default: return { text: paymentStatus.toUpperCase(), color: 'bg-gray-100 text-gray-800' };
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch(method) {
      case 'cod': return <DollarSign className="w-5 h-5 text-green-600" />;
      case 'gcash': return <Smartphone className="w-5 h-5 text-blue-600" />;
      case 'paymaya': return <Wallet className="w-5 h-5 text-purple-600" />;
      default: return <CreditCard className="w-5 h-5 text-gray-600" />;
    }
  };

  const getPaymentMethodName = (method: string) => {
    switch(method) {
      case 'cod': return 'Cash on Delivery';
      case 'gcash': return 'GCash';
      case 'paymaya': return 'PayMaya';
      default: return method;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const getStatusStep = (order: OrderDisplay): number => {
    const status = order.status?.toLowerCase() || '';
    const paymentStatus = order.paymentStatus?.toLowerCase() || '';
    
    // COD flow: pending → processing → shipped/out for delivery → delivered
    if (order.paymentMethod === 'cod') {
      if (status === 'shipped' || status === 'outfordelivery') return 3;
      if (status === 'delivered') return 4;

      const codSteps = ['pending', 'processing'];
      const idx = codSteps.indexOf(status);
      return idx >= 0 ? idx + 1 : 1;
    }
  
  // Digital payment flow: pending → awaiting_payment → processing → shipped → delivered
  const digitalSteps = ['pending', 'awaiting_payment', 'processing', 'shipped', 'outfordelivery', 'delivered'];
  
  // If payment proof uploaded and pending, still at payment step
  if (order.paymentProofImage && paymentStatus === 'pending' && !order.approvedBy) {
    return 2; // Payment step
  }
  
  // If payment approved, advance to processing
  if (order.approvedBy && paymentStatus === 'paid' && status === 'processing') {
    return 3; // Processing
  }
  
  // Shipped
  if (status === 'shipped' || status === 'outfordelivery') {
    return 4; // Shipped
  }
  
  // Delivered
  if (status === 'delivered') {
    return 5; // Delivered
  }
  
  const idx = digitalSteps.indexOf(status);
  return idx >= 0 ? idx + 1 : 1; // Default to first step
};

const getProgressSteps = (order: OrderDisplay) => {
  if (order.paymentMethod === 'cod') {
    return ['Order Placed', 'Processing', 'Shipped', 'Delivered'];
  }
  return ['Order Placed', 'Payment', 'Processing', 'Shipped', 'Delivered'];
};

const getTotalSteps = (order: OrderDisplay) => {
  return order.paymentMethod === 'cod' ? 4 : 5;
};

  const getNotification = () => {
    if (!order) return null;
    
    if (order.approvedBy && order.paymentStatus === 'paid') {
      return {
        type: 'approved',
        icon: <ThumbsUp className="w-6 h-6 text-green-600" />,
        title: 'Payment Approved!',
        message: `Your payment has been approved. Your order is now being processed.`,
        color: 'bg-green-50 border-green-200 text-green-800'
      };
    }
    
    if (order.rejectedBy && order.paymentStatus === 'failed') {
      return {
        type: 'rejected',
        icon: <ThumbsDown className="w-6 h-6 text-red-600" />,
        title: 'Payment Rejected',
        message: order.rejectionReason 
          ? `Reason: ${order.rejectionReason}` 
          : 'Your payment was rejected due to invalid or insufficient payment proof.',
        color: 'bg-red-50 border-red-200 text-red-800'
      };
    }
    
    // Check for approved payments first (for GCash/PayMaya)
    if (order.approvedBy && order.paymentStatus === 'paid' && order.paymentMethod !== 'cod') {
      return {
        type: 'processing',
        icon: <Package className="w-6 h-6 text-blue-600" />,
        title: 'Payment Approved - Order Processing',
        message: 'Your payment has been verified and your order is now being prepared for shipment.',
        color: 'bg-green-50 border-green-200 text-green-800'
      };
    }
    
    if (order.status === 'cancelled') {
      return {
        type: 'cancelled',
        icon: <XCircle className="w-6 h-6 text-red-600" />,
        title: 'Order Cancelled',
        message: order.rejectionReason || 'Your order has been cancelled.',
        color: 'bg-red-50 border-red-200 text-red-800'
      };
    }
    
    if (order.status === 'awaiting_payment' || (order.paymentStatus === 'pending' && !order.paymentProofImage)) {
      return {
        type: 'awaiting_payment',
        icon: <Clock className="w-6 h-6 text-orange-600" />,
        title: 'Complete Your Payment',
        message: 'Please complete your payment and upload the receipt to process your order.',
        color: 'bg-orange-50 border-orange-200 text-orange-800',
        action: true
      };
    }
    
    if (order.paymentStatus === 'pending' && order.paymentProofImage) {
      return {
        type: 'verifying',
        icon: <Clock className="w-6 h-6 text-blue-600" />,
        title: 'Payment Under Review',
        message: 'Your payment proof has been submitted and is awaiting verification. This usually takes 5-10 minutes.',
        color: 'bg-blue-50 border-blue-200 text-blue-800'
      };
    }
    
    if (order.status === 'processing') {
      return {
        type: 'processing',
        icon: <Package className="w-6 h-6 text-blue-600" />,
        title: 'Order Processing',
        message: 'Your order is being prepared for shipment.',
        color: 'bg-blue-50 border-blue-200 text-blue-800'
      };
    }
    
    if (order.status === 'shipped') {
      return {
        type: 'shipped',
        icon: <Truck className="w-6 h-6 text-blue-600" />,
        title: 'Order Shipped!',
        message: order.trackingNumber 
          ? `Your order has been shipped. Tracking number: ${order.trackingNumber}`
          : 'Your order has been shipped and is on its way!',
        color: 'bg-blue-50 border-blue-200 text-blue-800'
      };
    }
    
    if (order.status === 'delivered') {
      return {
        type: 'delivered',
        icon: <CheckCircle className="w-6 h-6 text-green-600" />,
        title: 'Order Delivered!',
        message: 'Your order has been delivered. We hope you love your items! Please take a moment to rate your products.',
        color: 'bg-green-50 border-green-200 text-green-800'
      };
    }
    
    return null;
  };

  const notification = getNotification();
  const paymentStatusDisplay = getPaymentStatusDisplay(order?.paymentStatus || '', order?.rejectedBy);
  const displayStatus = getDisplayStatus();
  const displayStatusColor = getDisplayStatusColor();
  const needsPayment = order && order.paymentMethod !== 'cod' && 
                       order.paymentStatus === 'pending' && 
                       !order.paymentProofImage;
  const paymentDetails = order ? SHOP_PAYMENT_DETAILS[order.paymentMethod as 'gcash' | 'paymaya'] : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-6">{error || "We couldn't find the order you're looking for."}</p>
          <button
            onClick={() => navigate('/account/orders')}
            className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition shadow-lg shadow-red-200"
          >
            Back to Orders
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    );
  }

  const currentStep = getStatusStep(order);
  const progressSteps = getProgressSteps(order);
  const progressStepsCount = getTotalSteps(order);
  const progressPercent = (() => {
    const steps = progressStepsCount - 1;
    const raw = steps > 0 ? ((currentStep - 1) / steps) * 100 : 0;
    return Math.max(0, Math.min(100, raw));
  })();
  const isPaymentFailed = order?.rejectedBy && order.paymentStatus === 'failed';
  const isDelivered = order?.status?.toLowerCase() === 'delivered';
  const isPaid = order?.paymentStatus?.toLowerCase() === 'paid';
  const canShowRating = isDelivered && (isPaid || order?.paymentMethod === 'cod');

  return (
    <div className="min-h-screen bg-gray-50 py-8 md:py-12">
      <div className="container mx-auto px-4 max-w-5xl">
        <button
          onClick={() => navigate('/account/orders')}
          className="flex items-center text-gray-500 hover:text-gray-700 mb-6 group"
        >
          <ChevronLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition" />
          Back to Orders
        </button>

        {notification && (
          <div className={`mb-6 p-5 rounded-2xl border ${notification.color} shadow-sm animate-in slide-in-from-top duration-300`}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                {notification.icon}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-1">{notification.title}</h3>
                <p className="text-sm leading-relaxed">{notification.message}</p>
                {notification.action && needsPayment && (
                  <button
                    onClick={() => setShowCompletePaymentModal(true)}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
                  >
                    <Upload className="w-4 h-4" />
                    Complete Payment Now
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-red-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Order #{order.orderNumber}</h1>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <Calendar className="w-4 h-4" />
                <span>Placed on {formatDate(order.orderDate)}</span>
              </div>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${displayStatusColor}`}>
              {displayStatus}
            </span>
          </div>

          {/* Order Progress Tracker */}
          <div className="mt-10 bg-gradient-to-r from-gray-50 to-white rounded-2xl p-8 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-8 text-center">Order Progress</h3>
            
            <div className="relative px-4">
              {/* Progress line background */}
              <div className="absolute top-5 left-8 right-8 h-1 bg-gray-200 rounded-full"></div>
              
              {/* Progress line foreground */}
              <div
                className="absolute top-5 left-8 h-1 bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all duration-500 ease-out"
                style={{ width: `calc(${progressPercent}% - 8px)` }}
              ></div>

              {/* Step circles */}
              <div className="flex items-start justify-between relative z-10">
                {progressSteps.map((step, index) => {
                  let stepCompleted = index < currentStep - 1;

                  if (order.status?.toLowerCase() === 'delivered' && index === progressSteps.length - 1) {
                    stepCompleted = true;
                  }
                  
                  if (order.paymentMethod !== 'cod' && index === 1) {
                    if (isPaymentFailed) {
                      stepCompleted = false;
                    } else if (order.approvedBy && order.paymentStatus === 'paid') {
                      stepCompleted = true;
                    } else if (order.paymentProofImage && order.paymentStatus === 'pending') {
                      stepCompleted = true;
                    }
                  }
                  
                  const isCurrent = index === currentStep - 1;
                  
                  let stepColor = 'bg-white border-2 border-gray-300 text-gray-400';
                  let iconEl = <span className="text-xs font-bold">{index + 1}</span>;
                  
                  if (isPaymentFailed && index === 1) {
                    stepColor = 'bg-red-500 text-white shadow-red-200';
                    iconEl = <XCircle className="w-5 h-5" />;
                  } else if (stepCompleted) {
                    stepColor = 'bg-red-600 text-white shadow-red-200';
                    iconEl = <CheckCircle className="w-5 h-5" />;
                  } else if (isCurrent) {
                    stepColor = 'bg-blue-500 text-white shadow-blue-200 animate-pulse';
                    iconEl = <Clock className="w-5 h-5 animate-spin" />;
                  }
                  
                  let labelColor = 'text-gray-400';
                  if (isPaymentFailed && index === 1) labelColor = 'text-red-600';
                  else if (stepCompleted) labelColor = order.status?.toLowerCase() === 'delivered' && index === progressSteps.length - 1 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold';
                  else if (isCurrent) labelColor = 'text-blue-600 font-semibold';

                  return (
                    <div key={step} className="flex flex-col items-center" style={{ width: '60px' }}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm ${stepColor}`}>
                        {iconEl}
                      </div>
                      <span className={`text-[11px] mt-2 text-center leading-tight ${labelColor}`}>
                        {step}
                      </span>
                      {isCurrent && (
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1 animate-ping"></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center mr-3">
                  <Package className="w-5 h-5 text-red-600" />
                </div>
                Order Items ({order.items?.length || 0})
              </h2>
              <div className="space-y-6">
                {order.items?.map((item, idx) => {
                  const hasRated = existingReviews.has(item.productId);
                  const userRating = ratedProducts.get(item.productId);

                  return (
                    <div key={idx} className="bg-gradient-to-r from-gray-50 to-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-all duration-200">
                      <div className="flex gap-6">
                        <div className="w-24 h-24 bg-gray-100 rounded-2xl overflow-hidden flex-shrink-0 shadow-sm">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.productName} className="w-full h-full object-cover hover:scale-105 transition-transform duration-200" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-10 h-10 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-2">{item.productName}</h3>
                          <div className="flex items-center gap-6 text-sm text-gray-600 mb-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Quantity:</span>
                              <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-semibold">{item.quantity}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Price:</span>
                              <span className="text-gray-900 font-semibold">₱{(item.unitPrice || item.price || 0).toLocaleString()}</span>
                            </div>
                          </div>

                          {canShowRating && (
                            <div className="mt-4">
                              {hasRated ? (
                                <div className="flex items-center gap-3 bg-green-50 px-4 py-2 rounded-xl border border-green-100">
                                  {renderStars(userRating?.rating || 0, 4)}
                                  <span className="text-sm text-green-700 font-medium flex items-center gap-1">
                                    <Check className="w-4 h-4" />
                                    You rated this product
                                  </span>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleRateProduct(item)}
                                  className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-all duration-200 group bg-red-50 hover:bg-red-100 px-4 py-2 rounded-xl border border-red-100 hover:border-red-200"
                                >
                                  <Star className="w-5 h-5 group-hover:fill-yellow-400 group-hover:text-yellow-400 transition-colors" />
                                  <span className="font-medium">Rate this product</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500 mb-1">Subtotal</div>
                          <p className="text-2xl font-bold text-red-600">
                            {formatCurrency((item.unitPrice || item.price || 0) * item.quantity)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 bg-gradient-to-r from-gray-50 to-white rounded-2xl p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-red-600" />
                  Order Summary
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 font-medium">Subtotal</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(order.totalAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 font-medium">Shipping</span>
                    <span className="text-green-600 font-semibold flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Free
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-gray-900">Total Amount</span>
                      <span className="text-2xl font-bold text-red-600">{formatCurrency(order.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {needsPayment && paymentDetails && (
              <div className="bg-white rounded-2xl shadow-sm border border-orange-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Complete Your Payment</h3>
                    <p className="text-xs text-gray-500">Pay via {paymentDetails.name}</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Amount to Pay:</span>
                    <span className="text-xl font-bold text-gray-900">{formatCurrency(order.totalAmount)}</span>
                  </div>
                  <button
                    onClick={() => setShowPaymentDetails(!showPaymentDetails)}
                    className="w-full flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <QrCode className="w-4 h-4" />
                    {showPaymentDetails ? 'Hide' : 'View'} Payment Details
                    <ChevronRight className={`w-4 h-4 transition ${showPaymentDetails ? 'rotate-90' : ''}`} />
                  </button>
                  
                  {showPaymentDetails && (
                    <div className="mt-4 space-y-3">
                      <div className="text-center">
                        <img src={paymentDetails.qrCode} alt="QR Code" className="w-40 h-40 mx-auto rounded-xl" />
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500">Account Name</p>
                        <p className="font-medium">{paymentDetails.accountName}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500">Account Number</p>
                        <p className="font-mono font-medium">{paymentDetails.accountNumber}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => setShowCompletePaymentModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition"
                >
                  <Upload className="w-4 h-4" />
                  Upload Payment Receipt
                </button>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center mr-3">
                  <CreditCard className="w-5 h-5 text-red-600" />
                </div>
                Payment Information
              </h3>

              <div className="space-y-4">
                <div className="bg-gradient-to-r from-gray-50 to-white p-4 rounded-2xl border border-gray-100">
                  <div className="flex items-center gap-3">
                    {getPaymentMethodIcon(order.paymentMethod)}
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Payment Method</p>
                      <p className="font-bold text-gray-900 capitalize">{getPaymentMethodName(order.paymentMethod)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-gray-50 to-white p-4 rounded-2xl border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Payment Status</p>
                      <p className="font-bold text-gray-900">{paymentStatusDisplay.text}</p>
                    </div>
                    <span className={`px-4 py-2 rounded-full text-sm font-bold ${paymentStatusDisplay.color} shadow-sm`}>
                      {paymentStatusDisplay.text}
                    </span>
                  </div>
                </div>

              {order.paymentStatus === 'failed' && order.rejectionReason && (
                <div className="mb-3 p-3 bg-red-50 rounded-lg border border-red-100">
                  <p className="text-sm text-red-700">
                    <span className="font-semibold">Reason:</span> {order.rejectionReason}
                  </p>
                </div>
              )}
              
              {order.approvedBy && order.paymentStatus === 'paid' && (
                <div className="mt-3 p-3 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-green-800">Payment Approved</p>
                      <p className="text-xs text-green-700 mt-1">
                        Approved by: <span className="font-medium">{order.approvedBy}</span>
                      </p>
                      {order.approvedAt && (
                        <p className="text-xs text-green-600 mt-0.5">
                          Date: {new Date(order.approvedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {order.rejectedBy && order.paymentStatus === 'failed' && (
                <div className="mt-3 p-4 bg-red-50 rounded-xl border border-red-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <XCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-bold text-red-800 mb-2">Payment Rejected</p>
                      
                      <div className="space-y-2 text-sm">
                        {order.rejectionReason && (
                          <div className="flex items-start gap-2">
                            <span className="text-red-600 font-medium min-w-[80px]">Reason:</span>
                            <span className="text-gray-800 bg-white px-3 py-2 rounded-lg border border-red-100 flex-1">
                              {order.rejectionReason}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-red-200">
                        <button
                          onClick={() => setShowCompletePaymentModal(true)}
                          className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                        >
                          <Upload className="w-4 h-4" />
                          Resubmit Payment Proof
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {order.paymentProofImage && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-2">Payment Proof</p>
                  <div 
                    className="relative group cursor-pointer overflow-hidden rounded-xl"
                    onClick={() => setShowReceiptModal(true)}
                  >
                    <img
                      src={order.paymentProofImage}
                      alt="Payment receipt"
                      className="w-full h-32 object-cover rounded-xl border border-gray-200 group-hover:scale-105 transition duration-300"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <Receipt className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  {order.paymentProofReference && (
                    <div className="mt-2 p-2 bg-gray-50 rounded-lg text-xs text-gray-500">
                      <span className="font-medium">Reference:</span> {order.paymentProofReference}
                    </div>
                  )}
                  {order.paymentProofSender && (
                    <div className="mt-1 p-2 bg-gray-50 rounded-lg text-xs text-gray-500">
                      <span className="font-medium">Sender:</span> {order.paymentProofSender}
                    </div>
                  )}
                </div>
              )}

              {order.deliveryProofImageUrl && order.status?.toLowerCase() === 'delivered' && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-2">Delivery Proof</p>
                  <div className="relative group overflow-hidden rounded-xl">
                    <img
                      src={order.deliveryProofImageUrl}
                      alt="Delivery proof"
                      className="w-full h-32 object-cover rounded-xl border border-gray-200"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center mr-3">
                  <MapPin className="w-5 h-5 text-red-600" />
                </div>
                Shipping Address
              </h3>
              <div className="bg-gradient-to-r from-gray-50 to-white p-6 rounded-2xl border border-gray-100">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line font-medium">{order.shippingAddress}</p>
              </div>
              {order.trackingNumber && (
                <div className="mt-6 bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-2xl border border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <Truck className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-700 font-bold">Tracking Number</p>
                      <p className="text-lg font-mono font-bold text-blue-800">{order.trackingNumber}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center mr-3">
                  <User className="w-5 h-5 text-red-600" />
                </div>
                Customer Details
              </h3>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-gray-50 to-white p-4 rounded-2xl border border-gray-100">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Full Name</p>
                      <p className="font-semibold text-gray-900">{order.customerName || 'Guest'}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-gray-50 to-white p-4 rounded-2xl border border-gray-100">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Email Address</p>
                      <p className="font-semibold text-gray-900">{order.customerEmail}</p>
                    </div>
                  </div>
                </div>
                {order.customerPhone && (
                  <div className="bg-gradient-to-r from-gray-50 to-white p-4 rounded-2xl border border-gray-100">
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Phone Number</p>
                        <p className="font-semibold text-gray-900">{order.customerPhone}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center mr-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                Need Help?
              </h3>
              <div className="space-y-4">
                {order.status === 'delivered' && (
                  <button className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-2xl hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg shadow-green-200 hover:shadow-xl hover:shadow-green-300 transform hover:-translate-y-0.5 font-semibold">
                    <Download className="w-5 h-5" />
                    Download Invoice
                  </button>
                )}
                <Link
                  to="/contact"
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-2xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-lg shadow-gray-200 hover:shadow-xl hover:shadow-gray-300 transform hover:-translate-y-0.5 font-semibold"
                >
                  <MessageCircle className="w-5 h-5" />
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Complete Payment Modal */}
      {showCompletePaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Complete Payment</h3>
              <button
                onClick={() => setShowCompletePaymentModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                <p className="text-sm text-orange-800">
                  <span className="font-bold">Amount to Pay:</span> {formatCurrency(order.totalAmount)}
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  Please send the exact amount and upload your receipt below.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Receipt <span className="text-red-600">*</span>
                </label>
                {!receiptPreview ? (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition"
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Camera className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-600">Click to upload receipt</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="relative">
                    <img 
                      src={receiptPreview} 
                      alt="Receipt preview" 
                      className="w-full max-h-48 object-contain bg-gray-50 rounded-xl border-2 border-green-500 p-2"
                    />
                    <button
                      onClick={removeReceipt}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reference Number <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="e.g., 1234 5678 9012"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sender Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Name on the transaction"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Date <span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                />
              </div>
              
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                <p className="text-xs text-blue-700">
                  Your payment will be reviewed by our team. This usually takes 5-10 minutes.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleCompletePayment}
                  disabled={submittingPayment}
                  className="flex-1 bg-orange-600 text-white py-2.5 rounded-xl font-medium hover:bg-orange-700 transition disabled:opacity-50"
                >
                  {submittingPayment ? (
                    <Loader className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    'Submit Payment Proof'
                  )}
                </button>
                <button
                  onClick={() => setShowCompletePaymentModal(false)}
                  className="px-6 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Rate Product</h3>
              <button
                onClick={() => setShowRatingModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0">
                  {selectedProduct.imageUrl ? (
                    <img src={selectedProduct.imageUrl} alt={selectedProduct.productName} className="w-full h-full object-cover" />
                  ) : (
                    <Package className="w-8 h-8 text-gray-400 m-4" />
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{selectedProduct.productName}</h4>
                  <p className="text-sm text-gray-500">Quantity: {selectedProduct.quantity}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating *</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setSelectedRating(rating)}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          rating <= selectedRating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Review Title (Optional)</label>
                <input
                  type="text"
                  value={ratingTitle}
                  onChange={(e) => setRatingTitle(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                  placeholder="Summarize your experience"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Review *</label>
                <textarea
                  rows={4}
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition resize-none"
                  placeholder="Share your thoughts about this product..."
                  required
                />
              </div>
              
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                <p className="text-xs text-amber-700">
                  Your feedback helps other customers make informed decisions. Thank you for sharing your experience!
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSubmitRating}
                  disabled={submittingRating}
                  className="flex-1 bg-red-600 text-white py-2.5 rounded-xl font-medium hover:bg-red-700 transition disabled:opacity-50"
                >
                  {submittingRating ? 'Submitting...' : 'Submit Review'}
                </button>
                <button
                  onClick={() => setShowRatingModal(false)}
                  className="px-6 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && order.paymentProofImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowReceiptModal(false)}
        >
          <button
            onClick={() => setShowReceiptModal(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 bg-black/50 rounded-full p-2 transition"
          >
            <XCircle className="w-8 h-8" />
          </button>
          <img
            src={order.paymentProofImage}
            alt="Receipt full size"
            className="max-w-full max-h-full object-contain rounded-2xl"
          />
        </div>
      )}
    </div>
  </div>
);
};

export default OrderDetail;
